/** Правила, формула индекса и расчёт выигрыша — доступны до входа в комнату (п. 9.2 ТЗ). */
import type { ServerInfo } from '@mt/protocol';
import { cr } from '../lib/format';
import { Dialog } from './Dialog';

export function Rules({ info, onClose }: { info: ServerInfo; onClose: () => void }) {
  const cap = info.commissionCapAntesPerRound;
  return (
    <Dialog title="Правила «Рыночного стола»" onClose={onClose} wide>
      <div className="rules">
        <p className="demo-note">Демо с игровыми кредитами. Реальных денег, платежей, покупки или вывода кредитов нет. Не является торговой рекомендацией.</p>
        <h3>Игровой индекс</h3>
        <p>
          Перед каждым розыгрышем сервер случайно выбирает три разные акции из открытого набора ликвидных акций S&amp;P 500. Индекс — равновзвешенная комбинация их процентных изменений относительно закрытия бара P₀:
        </p>
        <pre className="formula">I(t) = I₀ · (1 + ⅓ · Σ (pᵢ(t) / Bᵢ − 1)),   I₀ = P₀ = {info.indexBase.toFixed(2)}</pre>
        <ul>
          <li>Bᵢ — цена закрытия акции на баре P₀; шаг цены {String(info.tick).replace('.', ',')} пункта; округление к ближайшему шагу, половина — вверх.</li>
          <li>O/C бара — индекс по открытиям/закрытиям; H/L — по максимумам/минимумам компонентов (оценка сверху/снизу), но не уже O и C.</li>
          <li>Минута без сделок по акции — используется её последняя цена.</li>
          <li>«Активность» — средний по трём акциям объём минуты, делённый на средний объём акции в окне истории (1 = обычно). VWAP* взвешен по активности.</li>
          <li>Формула открыта; скрыт только состав тройки — до третьего круга. Хеш-обязательство публикуется до ставок и раскрывается после расчёта.</li>
        </ul>
        <h3>Ход розыгрыша</h3>
        <ol>
          <li>Взнос N вносится в банк, фиксируется P₀.</li>
          <li>Круг 1: график без шкалы, выбор направления (выше/ниже) и ставки.</li>
          <li>Круг 2: открывается шкала цен и расстояний в пунктах. Направление можно оставить или изменить.</li>
          <li>Круг 3: открывается состав. Направление уже не меняется — укажите строго положительное число пунктов (шаг {String(info.tick).replace('.', ',')}).</li>
          <li>Фиксация. P₁ — закрытие следующей минутной свечи; Δ = P₁ − P₀.</li>
        </ol>
        <h3>Действия</h3>
        <p>
          Пас, чек, ставка, колл, райз (не более одного повышения после первой ставки в круге), олл-ин (с побочными банками). Время на ход — {Math.round(info.turnMs / 1000)} с; по тайм-ауту — чек, если возможно, иначе пас. К концу круга неуравнённая часть ставки возвращается, не уравнявшие ставку пасуют. Без полного прогноза к фиксации участник выбывает.
        </p>
        <h3>Победители и выплаты</h3>
        <ul>
          <li>Неверное направление — всегда проигрыш, даже при точном числе пунктов.</li>
          <li>
            Для угадавших: ошибка = |прогноз − |Δ||; вес = вклад_в_банк / (1 + ошибка / {String(info.tick).replace('.', ',')}). Банк делится пропорционально весам; остатки 0,01 кр. — методом наибольших остатков, при равенстве — по порядку хода.
          </li>
          <li>Каждый побочный банк рассчитывается отдельно; на него претендуют только внёсшие в него и не спасовавшие. Банк, где нет угадавших, возвращается внёсшим.</li>
          <li>Отмена с полным возвратом и нулевой комиссией: P₁ = P₀; никто из оставшихся не угадал направление; нет достоверных данных.</li>
          <li>Все, кроме одного, спасовали — оставшийся забирает банк без проверки прогноза.</li>
        </ul>
        <h3>Пример</h3>
        <p>
          Четыре игрока, взнос 10 кр.; Δ = +2,00. Игрок A: «выше 2,00» (ошибка 0, вес 10), B: «выше 2,25» (ошибка 0,25 = 1 шаг, вес 10/2 = 5), C и D — «ниже». Банк 40 кр., комиссия 5% = 2 кр.; к распределению 38 кр.: A — 25,33 кр., B — 12,67 кр.
        </p>
        <h3>Комиссия</h3>
        <p>
          {info.commissionBps / 100}% от новых кредитов, внесённых в банк в каждом круге (в первом — вместе со взносами). Ранее внесённое и возвращённое не облагается; при отмене комиссия не удерживается.
          {cap !== null && ` Потолок — ${cap === 1 ? 'один взнос' : `${cap} взноса`} N за круг, то есть не более ${3 * cap}N за розыгрыш (как потолок рейка в покере).`} Удержанное поступает на отдельный счёт банка демо.
        </p>
        <h3>Горячие клавиши</h3>
        <p className="mono small">↑/↓ направление · +/− пункты (Shift ×4) · F пас · C чек/колл · B ставка/райз · A олл-ин · Enter подтвердить · Esc отмена</p>
      </div>
    </Dialog>
  );
}
